import { onScopeDispose, ref, shallowRef, watch } from 'vue'
import { listVolcMentionAssets } from '@/api/volcAsset'

import { getToken } from '@/api/auth'
import { mentionAssetsCache, mentionAccountScope } from './mentionAssetsCache'

const toMentionItems = data => data.map(asset => ({
  id: `volc-${asset.assetId}`,
  tableId: asset.tableId,
  uuid: asset.name || asset.assetId,
  coverUrl: asset.sourceUrl || '',
  instructionValue: asset.assetUri || '',
  status: 999,
  type: 'volc-asset',
  assetUri: asset.assetUri,
  sourceUrl: asset.sourceUrl || '',
  duration: Number(asset.durationMs) > 0 ? Number(asset.durationMs) / 1000 : 0,
  assetId: asset.assetId,
  name: asset.name || '',
  isRealPerson: asset.isRealPerson !== 0,
  assetType: asset.assetType || 'Image',
  mediaUrl: asset.sourceUrl || ''
}))

export function useVolcMentionAssets(getScriptId, canView) {
  const assets = shallowRef([])
  const loading = ref(false), error = ref('')
  let pending = null
  let version = 0
  let disposed = false
  let identity = null

  const load = ({ force = false, silent = false } = {}) => {
    if (disposed || !canView.value) return Promise.resolve()
    const credential = getToken(), account = mentionAccountScope()
    if (!credential) { assets.value = []; loading.value = false; error.value = ''; return Promise.resolve() }
    const key = `mentions:${getScriptId() || 'all'}`
    if (identity?.credential !== credential || identity?.account !== account) {
      version++
      pending = null
      assets.value = []
      identity = { credential, account }
    }
    if (!force && pending && pending.credential === credential && pending.account === account) return pending
    const cached = !force && mentionAssetsCache.peek(key)
    if (cached?.fresh) { assets.value = toMentionItems(cached.data); error.value = ''; return Promise.resolve() }

    const requestVersion = ++version
    loading.value = true
    error.value = ''
    const scriptId = getScriptId()
    const isCurrent = () => !disposed && requestVersion === version &&
      scriptId === getScriptId() && canView.value && credential === getToken() && account === mentionAccountScope()

    pending = (async () => {
      try {
        const data = await mentionAssetsCache.load(key, async () => {
          const res = await listVolcMentionAssets(scriptId)
          if (res.code !== 200) throw Object.assign(new Error(res.message || 'Asset list unavailable'), { code: res.code })
          return res.data || []
        }, { force })
        if (isCurrent() && data) assets.value = toMentionItems(data)
      } catch (cause) {
        if (isCurrent()) error.value = '资产加载失败，请重试'
        if (isCurrent() && [401, 403].includes(Number(cause?.code || cause?.response?.status))) assets.value = []
        if (isCurrent() && !silent) console.warn('加载虚拟人像资产失败', cause)
      } finally {
        if (requestVersion === version) { pending = null; loading.value = false }
      }
    })()
    pending.credential = credential
    pending.account = account
    return pending
  }

  watch([getScriptId, canView], () => {
    version++
    pending = null
    assets.value = []
    loading.value = false
    error.value = ''
    if (!canView.value) mentionAssetsCache.invalidate()
    void load({ silent: true })
  }, { immediate: true, flush: 'sync' })

  onScopeDispose(() => {
    disposed = true
    version++
    pending = null
  })

  return { assets, load, loading, error }
}
